import React, { useEffect } from 'react';
import { ConfigProvider } from 'antd';
import antdTheme from 'antd/es/theme';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { DESIGN_COMPONENTS, DESIGN_TOKEN } from './designTokens';

/**
 * Only override tokens according to ClinicDesignTemplatePage (border, font, spacing, shadow).
 * Keep all default Ant Design CSS (including disabled state) through the algorithm.
 */

/** Ensure disabled state always has styles (keep antd defaults when derived by algorithm) */
const DISABLED_TOKEN = {
  colorTextDisabled: 'rgba(0, 0, 0, 0.5)',
  colorBgContainerDisabled: 'rgba(0, 0, 0, 0.04)',
  colorBorderDisabled: 'rgba(0, 0, 0, 0.06)',
} as const;

export const AntdThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors } = useTheme();

  useEffect(() => {
    document.documentElement.style.setProperty('--app-theme-primary', colors.primary);
    document.documentElement.style.setProperty('--app-theme-primary-hover', colors.primaryHover);
  }, [colors.primary, colors.primaryHover]);

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: colors.primary,
          colorLink: colors.primary,
          colorTextBase: '#111827',
          ...DISABLED_TOKEN,
          ...DESIGN_TOKEN,
        },
        components: {
          Button: {
            colorPrimary: colors.primary,
            colorPrimaryHover: colors.primaryHover,
            ...DESIGN_COMPONENTS.Button,
          },
          Input: DESIGN_COMPONENTS.Input,
          Select: DESIGN_COMPONENTS.Select,
          Modal: {
            titleFontSize: 18,
            titleLineHeight: 1.3,
            headerBg: colors.sectionHeader,
            titleColor: colors.sectionHeaderText,
          },
          Tabs: {
            horizontalItemPadding: '8px 16px',
            titleFontSize: 14,
            cardPadding: '16px',
          },
          Pagination: {
            itemSize: 32,
          },
          Table: {
            headerBg: colors.sectionHeader,
            headerColor: '#ffffff',
            borderColor: '#9ca3af',
            headerSplitColor: 'rgba(255, 255, 255, 0.2)',
            cellPaddingBlock: 12,
            cellPaddingInline: 20,
            cellFontSize: 14,
            headerSortActiveBg: colors.sectionHeader,
            headerSortHoverBg: colors.sectionHeader,
            colorIcon: '#ffffff',
            colorIconHover: '#ffffff',
          },
          Card: {
            bodyPadding: 16,
            headerPadding: 12,
            headerFontSize: 16,
          },
          Alert: {
            defaultPadding: '16px',
          },
          Badge: {
            textFontSize: 12,
          },
          Checkbox: {
            size: 16,
            borderRadiusSM: 4,
          },
          Radio: {
            size: 16,
            borderRadiusSM: 4,
          },
          Switch: {
            trackHeight: 24,
            trackMinWidth: 44,
            handleSize: 16,
            trackPadding: 4,
          },
          Progress: {
            lineBorderRadius: 9999,
          },
          Spin: {
            dotSize: 32,
          },
          Form: DESIGN_COMPONENTS.Form,
          Dropdown: {
            paddingBlock: 8,
          },
          Tooltip: {
            maxWidth: 384,
          },
          Menu: {
            itemSelectedBg: colors.menuActive,
            itemHoverBg: colors.menuHover,
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
};
